/**
 * ============================================================================
 * Planning 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Planning（计划管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createPlanningApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 📦 包含的功能：
 * 1. MRP运算（Material Requirements Planning）
 *    - 单BOM运算：计算单个BOM的物料净需求
 *    - 批量BOM运算：同时计算多个BOM的物料需求
 *    - 生产订单运算：根据生产订单计算物料需求
 *    - 采购建议提取：从MRP结果中提取采购建议
 * 
 * 2. 齐套分析（Kit Check / Material Availability）
 *    - 工单齐套检查：检查指定工单的物料是否齐全
 *    - BOM齐套检查：检查指定BOM的物料是否齐全（支持指定仓库）
 *    - 批量工单检查：批量检查多个工单的齐套情况
 *    - 汇总齐套检查：将多工单需求汇总后统一检查
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config),
 *   post: (url, data, config) => requestClient.post(url, data, config),
 *   put: (url, data, config) => requestClient.put(url, data, config),
 *   delete: (url, config) => requestClient.delete(url, config),
 * }
 * 
 * // 2. 调用工厂函数
 * import { createPlanningApi } from '@bayes-mes/shared/api/planning/factory'
 * export const planningApi = createPlanningApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * // MRP运算
 * const result = await planningApi.calculateMrp({ bom_id: 1, quantity: 100 })
 * console.log(result.summary.shortageMaterials)
 * 
 * // 齐套分析
 * const kitResult = await planningApi.checkWorkOrderKit(123, { warehouse_id: 5 })
 * console.log(kitResult.kitRate)
 * ```
 * 
 * @module api/planning/factory
 * @description Planning（计划管理）模块的 API 工厂函数
 * 
 * @source 对应后端文件:
 * - backend/app/Modules/Planning/Controllers/MrpController.php
 * - backend/app/Modules/Planning/Controllers/KitCheckController.php
 * - backend/app/Modules/Planning/Services/MrpCalculationService.php
 * - backend/app/Modules/Planning/Services/KitCheckService.php
 * 
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-12-09
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  // 响应类型
  OrderFulfillmentDetail,
  OrderFulfillmentListResult,
  MrpResult,
  PurchaseSuggestionSummary,
  KitCheckResult,
  KitCheckAggregatedResult,
  KitCheckBatchResult,
  KitCheckByPlanResult,
  
  // 请求类型
  MrpCalculateForm,
  MrpBatchCalculateForm,
  MrpCalculateForOrderForm,
  MrpSuggestionsForm,
  KitCheckBatchForm,
  KitCheckAggregatedForm,
  KitCheckByPlanForm,
  
  // 查询参数类型
  OrderFulfillmentQueryParams,
  KitCheckWorkOrderQueryParams,
  KitCheckBomQueryParams,
} from '../../types/planning'

import type { ApiResponse } from '../../types/api'

// 导入统一的请求客户端接口
import type { IRequestClient } from '../core/client'

// 导入 API 路由定义
import { API_V1_ROUTES } from '../routes/v1'

// ============================================================================
// 工厂函数
// ============================================================================

/**
 * 创建 Planning 模块 API
 * 
 * @param client - 请求客户端（实现 IRequestClient 接口）
 * @returns Planning 模块的所有 API 方法
 * 
 * @example
 * ```typescript
 * import { createPlanningApi } from '@bayes-mes/shared/api/planning/factory'
 * 
 * // 创建 API 实例
 * const planningApi = createPlanningApi(webAdapter)
 * 
 * // 使用 MRP 运算
 * const mrpResult = await planningApi.calculateMrp({
 *   bom_id: 1,
 *   quantity: 100,
 *   include_safety_stock: true,
 * })
 * 
 * // 使用齐套分析
 * const kitResult = await planningApi.checkWorkOrderKit(123)
 * ```
 */
export function createPlanningApi(client: IRequestClient) {
  return {
    // ========================================================================
    // 订单履约评估工作台
    // ========================================================================

    /**
     * 获取订单履约评估列表
     *
     * 以销售订单为主线，汇总成品库存、生产计划、已排未开工、真实WIP、
     * 工序进度、MRP缺料和采购到货，用于计划员判断交期风险。
     *
     * @api GET /api/v1/planning/order-fulfillment
     */
    getOrderFulfillmentList(
      params?: OrderFulfillmentQueryParams
    ): Promise<ApiResponse<OrderFulfillmentListResult>> {
      return client.get<ApiResponse<OrderFulfillmentListResult>>(
        API_V1_ROUTES.planning.orderFulfillment,
        { params }
      )
    },

    /**
     * 获取订单履约评估详情
     *
     * @api GET /api/v1/planning/order-fulfillment/{id}
     */
    getOrderFulfillmentDetail(
      id: number | string
    ): Promise<ApiResponse<OrderFulfillmentDetail>> {
      return client.get<ApiResponse<OrderFulfillmentDetail>>(
        API_V1_ROUTES.planning.orderFulfillmentDetail(id)
      )
    },

    // ========================================================================
    // MRP 运算相关 API
    // ========================================================================

    /**
     * 单BOM MRP运算
     * 
     * 根据指定的BOM和生产数量，计算物料净需求，考虑：
     * - 现有库存
     * - 在途采购（已确认/部分到货的采购订单）
     * - 预计入库（进行中的工单产出）
     * - 安全库存
     * 
     * @param data - MRP运算参数
     * @returns MRP运算结果，包含物料需求明细和采购建议
     * 
     * @example
     * ```typescript
     * const result = await planningApi.calculateMrp({
     *   bom_id: 1,
     *   quantity: 100,
     *   factory_id: 1,
     *   include_safety_stock: true,
     *   include_in_transit: true,
     *   include_planned_output: true,
     *   required_date: '2025-12-20',
     * })
     * 
     * console.log('缺料物料数:', result.summary.shortageMaterials)
     * console.log('严重缺料数:', result.summary.criticalMaterials)
     * 
     * // 遍历需要采购的物料
     * result.requirements
     *   .filter(r => r.netRequirement > 0)
     *   .forEach(r => {
     *     console.log(`${r.materialCode}: 需采购 ${r.netRequirement} ${r.unit}`)
     *   })
     * ```
     * 
     * @api POST /api/v1/planning/mrp/calculate
     */
    calculateMrp(data: MrpCalculateForm): Promise<ApiResponse<MrpResult>> {
      return client.post<ApiResponse<MrpResult>>(
        API_V1_ROUTES.planning.calculate,
        data
      )
    },

    /**
     * 批量BOM MRP运算
     * 
     * 同时计算多个BOM的物料需求，并汇总相同物料的需求量
     * 适用于生产计划排程时批量计算物料需求
     * 
     * @param data - 批量MRP运算参数（最多100个BOM）
     * @returns 汇总的MRP运算结果
     * 
     * @example
     * ```typescript
     * const result = await planningApi.calculateMrpBatch({
     *   items: [
     *     { bom_id: 1, quantity: 100 },
     *     { bom_id: 2, quantity: 50 },
     *     { bom_id: 3, quantity: 200 },
     *   ],
     *   factory_id: 1,
     *   include_safety_stock: true,
     * })
     * 
     * console.log('计算了', result.itemsCount, '个BOM')
     * console.log('总缺料物料数:', result.summary.shortageMaterials)
     * ```
     * 
     * @api POST /api/v1/planning/mrp/calculate-batch
     */
    calculateMrpBatch(data: MrpBatchCalculateForm): Promise<ApiResponse<MrpResult>> {
      return client.post<ApiResponse<MrpResult>>(
        API_V1_ROUTES.planning.calculateBatch,
        data
      )
    },

    /**
     * 生产订单MRP运算
     * 
     * 根据生产订单自动查找对应的BOM并计算物料需求
     * 订单的计划结束日期会作为需求日期筛选在途和预计入库
     * 
     * @param id - 生产订单ID
     * @param data - 运算选项（可选）
     * @returns MRP运算结果，包含生产订单信息
     * 
     * @example
     * ```typescript
     * const result = await planningApi.calculateMrpForOrder(123, {
     *   include_safety_stock: true,
     *   include_in_transit: true,
     * })
     * 
     * console.log('生产订单:', result.productionOrder?.orderNo)
     * console.log('需求日期:', result.productionOrder?.planEndDate)
     * console.log('缺料物料数:', result.summary.shortageMaterials)
     * ```
     * 
     * @api POST /api/v1/planning/mrp/calculate-for-order/{id}
     */
    calculateMrpForOrder(
      id: number,
      data?: MrpCalculateForOrderForm
    ): Promise<ApiResponse<MrpResult>> {
      return client.post<ApiResponse<MrpResult>>(
        API_V1_ROUTES.planning.calculateForOrder(id),
        data || {}
      )
    },

    /**
     * 提取采购建议
     * 
     * 从MRP运算结果中提取所有需要采购的物料建议
     * 按优先级分组统计
     * 
     * @param data - 包含MRP运算结果的请求体
     * @returns 采购建议汇总
     * 
     * @example
     * ```typescript
     * // 先执行MRP运算
     * const mrpResult = await planningApi.calculateMrp({ bom_id: 1, quantity: 100 })
     * 
     * // 提取采购建议
     * const suggestions = await planningApi.extractPurchaseSuggestions({
     *   mrp_result: mrpResult,
     * })
     * 
     * console.log('总建议数:', suggestions.totalCount)
     * console.log('紧急采购:', suggestions.urgentCount)
     * console.log('高优先级:', suggestions.highCount)
     * 
     * suggestions.suggestions.forEach(s => {
     *   console.log(`[${s.priorityText}] ${s.materialCode}: ${s.message}`)
     * })
     * ```
     * 
     * @api POST /api/v1/planning/mrp/suggestions
     */
    extractPurchaseSuggestions(
      data: MrpSuggestionsForm
    ): Promise<ApiResponse<PurchaseSuggestionSummary>> {
      return client.post<ApiResponse<PurchaseSuggestionSummary>>(
        API_V1_ROUTES.planning.suggestions,
        data
      )
    },

    // ========================================================================
    // 齐套分析相关 API
    // ========================================================================

    /**
     * 检查工单齐套情况
     * 
     * 检查指定工单的物料是否齐全，支持指定仓库（如线边仓）
     * 自动查找工单对应的BOM并展开物料需求
     * 
     * @param id - 工单ID
     * @param params - 查询参数（可选，如指定仓库）
     * @returns 齐套检查结果
     * 
     * @example
     * ```typescript
     * // 检查工单齐套（全局库存）
     * const result = await planningApi.checkWorkOrderKit(123)
     * console.log('齐套率:', result.kitRate, '%')
     * console.log('状态:', result.status) // READY/PARTIAL/WARNING/CRITICAL
     * 
     * // 检查工单齐套（指定线边仓）
     * const lineResult = await planningApi.checkWorkOrderKit(123, {
     *   warehouse_id: 5,
     * })
     * console.log('线边仓齐套率:', lineResult.kitRate, '%')
     * 
     * // 查看缺料明细
     * lineResult.shortageItems?.forEach(item => {
     *   console.log(`${item.materialCode}: 缺 ${item.shortage} ${item.unit}`)
     * })
     * ```
     * 
     * @api GET /api/v1/planning/kit/check-work-order/{id}
     */
    checkWorkOrderKit(
      id: number,
      params?: KitCheckWorkOrderQueryParams
    ): Promise<ApiResponse<KitCheckResult>> {
      return client.get<ApiResponse<KitCheckResult>>(
        API_V1_ROUTES.planning.detail(id),
        { params }
      )
    },

    /**
     * 检查BOM齐套情况
     * 
     * 检查指定BOM的物料是否齐全，支持指定生产数量和仓库
     * 
     * @param id - BOM ID
     * @param params - 查询参数（数量、工厂、仓库）
     * @returns 齐套检查结果
     * 
     * @example
     * ```typescript
     * // 检查BOM齐套
     * const result = await planningApi.checkBomKit(1, {
     *   quantity: 100,
     *   factory_id: 1,
     *   warehouse_id: 5, // 指定线边仓
     * })
     * 
     * console.log('BOM:', result.bomCode)
     * console.log('生产数量:', result.quantity)
     * console.log('齐套率:', result.kitRate, '%')
     * console.log('缺料物料数:', result.summary?.shortageMaterials)
     * ```
     * 
     * @api GET /api/v1/planning/kit/check-bom/{id}
     */
    checkBomKit(
      id: number,
      params?: KitCheckBomQueryParams
    ): Promise<ApiResponse<KitCheckResult>> {
      return client.get<ApiResponse<KitCheckResult>>(
        API_V1_ROUTES.planning.kitCheckBom(id),
        { params }
      )
    },

    /**
     * 批量工单齐套检查
     * 
     * 批量检查多个工单的齐套情况，每个工单独立检查
     * 返回各工单的检查结果和整体统计
     * 
     * @param data - 批量检查请求（工单ID数组，最多100个）
     * @returns 批量检查结果
     * 
     * @example
     * ```typescript
     * const result = await planningApi.checkKitBatch({
     *   work_order_ids: [123, 124, 125, 126],
     *   warehouse_id: 5, // 可选，指定线边仓
     * })
     * 
     * console.log('总工单数:', result.totalWorkOrders)
     * console.log('齐套工单数:', result.kitCompleteCount)
     * console.log('不齐套工单数:', result.kitIncompleteCount)
     * console.log('整体齐套率:', result.overallKitRate, '%')
     * 
     * // 查看各工单检查结果
     * result.results.forEach(r => {
     *   if ('error' in r) {
     *     console.log(`工单 ${r.workOrderId}: 错误 - ${r.error}`)
     *   } else {
     *     console.log(`工单 ${r.workOrder?.workOrderNo}: ${r.status}`)
     *   }
     * })
     * ```
     * 
     * @api POST /api/v1/planning/kit/check-batch
     */
    checkKitBatch(data: KitCheckBatchForm): Promise<ApiResponse<KitCheckBatchResult>> {
      return client.post<ApiResponse<KitCheckBatchResult>>(
        API_V1_ROUTES.planning.checkBatch,
        data
      )
    },

    /**
     * 多工单汇总齐套检查
     * 
     * 将多个工单的物料需求汇总后统一检查齐套情况
     * 适用于同批次生产的多工单齐套预检
     * 
     * @param data - 汇总检查请求（工单ID数组，最多100个）
     * @returns 汇总检查结果
     * 
     * @example
     * ```typescript
     * const result = await planningApi.checkKitAggregated({
     *   work_order_ids: [123, 124, 125],
     *   warehouse_id: 5,
     * })
     * 
     * console.log('涉及工单数:', result.workOrderCount)
     * console.log('汇总齐套率:', result.kitRate, '%')
     * console.log('总物料种类:', result.summary.totalMaterials)
     * console.log('缺料种类:', result.summary.shortageMaterials)
     * 
     * // 查看缺料明细（含来源工单信息）
     * result.shortageItems.forEach(item => {
     *   console.log(`${item.materialCode}: 缺 ${item.shortage} ${item.unit}`)
     *   item.sources?.forEach(s => {
     *     console.log(`  - 来自工单 ${s.workOrderNo}: ${s.quantity}`)
     *   })
     * })
     * ```
     * 
     * @api POST /api/v1/planning/kit/check-aggregated
     */
    checkKitAggregated(
      data: KitCheckAggregatedForm
    ): Promise<ApiResponse<KitCheckAggregatedResult>> {
      return client.post<ApiResponse<KitCheckAggregatedResult>>(
        API_V1_ROUTES.planning.checkAggregated,
        data
      )
    },

    /**
     * 按生产计划齐套检查
     * 
     * 按生产计划维度检查齐套情况，自动聚合计划下所有工单
     * 返回各计划的检查结果和整体统计
     * 
     * @param data - 按计划检查请求（计划ID数组，最多50个）
     * @returns 按计划检查结果
     * 
     * @example
     * ```typescript
     * const result = await planningApi.checkKitByPlan({
     *   plan_ids: [1, 2, 3],
     *   warehouse_id: 5, // 可选，指定仓库
     * })
     * 
     * console.log('总计划数:', result.totalPlans)
     * console.log('齐套计划数:', result.kitCompleteCount)
     * console.log('不齐套计划数:', result.kitIncompleteCount)
     * console.log('总工单数:', result.totalWorkOrders)
     * console.log('整体齐套率:', result.overallKitRate, '%')
     * 
     * // 查看各计划检查结果
     * result.results.forEach(r => {
     *   console.log(`计划 ${r.plan.planNo}: ${r.status}, 齐套率 ${r.kitRate}%`)
     * })
     * ```
     * 
     * @api POST /api/v1/planning/kit/check-by-plan
     */
    checkKitByPlan(data: KitCheckByPlanForm): Promise<ApiResponse<KitCheckByPlanResult>> {
      return client.post<ApiResponse<KitCheckByPlanResult>>(
        API_V1_ROUTES.planning.checkByPlan,
        data
      )
    },
  }
}

// ============================================================================
// 类型导出
// ============================================================================

/**
 * Planning API 类型
 * 
 * 用于类型标注或扩展
 */
export type PlanningApi = ReturnType<typeof createPlanningApi>
